import { ApiClient } from '@stuut/common/src/api_client';
import * as resources from './resources';
import axios from 'axios';
import type { Attachment, PresignedUrl } from '@api/models';

export class StuutApi extends ApiClient {
  get alertConfigs(): resources.AlertConfigs {
    return new resources.AlertConfigs(this);
  }

  get analytics(): resources.Analytics {
    return new resources.Analytics(this);
  }

  get apiRequestLogs(): resources.APIRequestLogs {
    return new resources.APIRequestLogs(this);
  }

  get apPortals(): resources.ApPortals {
    return new resources.ApPortals(this);
  }

  get apPortalAccounts(): resources.ApPortalAccounts {
    return new resources.ApPortalAccounts(this);
  }

  get apPortalSessions(): resources.ApPortalSessions {
    return new resources.ApPortalSessions(this);
  }

  get automatedTasks(): resources.AutomatedTasks {
    return new resources.AutomatedTasks(this);
  }

  get auth(): resources.Auth {
    return new resources.Auth(this);
  }

  get bankAccounts(): resources.BankAccounts {
    return new resources.BankAccounts(this);
  }

  get bankStatements(): resources.BankStatements {
    return new resources.BankStatements(this);
  }

  get bankTransactions(): resources.BankTransactions {
    return new resources.BankTransactions(this);
  }

  get callAgents(): resources.CallAgents {
    return new resources.CallAgents(this);
  }

  get callAgentScenarios(): resources.CallAgentScenarios {
    return new resources.CallAgentScenarios(this);
  }

  get calls(): resources.Calls {
    return new resources.Calls(this);
  }

  get cashApp(): resources.CashApp {
    return new resources.CashApp(this);
  }

  get cashAppGuidelines(): resources.CashAppGuidelines {
    return new resources.CashAppGuidelines(this);
  }

  get channels(): resources.Channels {
    return new resources.Channels(this);
  }

  get chats(): resources.Chats {
    return new resources.Chats(this);
  }

  get claims(): resources.Claims {
    return new resources.Claims(this);
  }

  get claimLineItems(): resources.ClaimLineItems {
    return new resources.ClaimLineItems(this);
  }

  get creditMemos(): resources.CreditMemos {
    return new resources.CreditMemos(this);
  }

  get disputes(): resources.Disputes {
    return new resources.Disputes(this);
  }

  get disputeStatusCounts(): resources.DisputeStatusCounts {
    return new resources.DisputeStatusCounts(this);
  }

  get disputeItems(): resources.DisputeItems {
    return new resources.DisputeItems(this);
  }

  get disputeReasons(): resources.DisputeReasons {
    return new resources.DisputeReasons(this);
  }

  get deductionsCreditMemos(): resources.DeductionsCreditMemos {
    return new resources.DeductionsCreditMemos(this);
  }

  get deductionsCreditMemoLineItems(): resources.DeductionsCreditMemoLineItems {
    return new resources.DeductionsCreditMemoLineItems(this);
  }

  get deductions(): resources.Deductions {
    return new resources.Deductions(this);
  }

  get deductionDocuments(): resources.DeductionDocuments {
    return new resources.DeductionDocuments(this);
  }

  get deductionReasons(): resources.DeductionReasons {
    return new resources.DeductionReasons(this);
  }

  get emailSenders(): resources.EmailSenders {
    return new resources.EmailSenders(this);
  }

  get emailTemplates(): resources.EmailTemplates {
    return new resources.EmailTemplates(this);
  }

  get emailTemplateFolders(): resources.EmailTemplateFolders {
    return new resources.EmailTemplateFolders(this);
  }

  get emailThreads(): resources.EmailThreads {
    return new resources.EmailThreads(this);
  }

  get emails(): resources.Emails {
    return new resources.Emails(this);
  }

  get files(): resources.Files {
    return new resources.Files(this);
  }

  get inbox(): resources.InboxStatus {
    return new resources.InboxStatus(this);
  }

  get integrations(): resources.Integrations {
    return new resources.Integrations(this);
  }

  get invoices(): resources.Invoices {
    return new resources.Invoices(this);
  }

  get inventoryItems(): resources.InventoryItems {
    return new resources.InventoryItems(this);
  }

  get outreachWorkflowStagePartnerEvals(): resources.OutreachWorkflowStagePartnerEvals {
    return new resources.OutreachWorkflowStagePartnerEvals(this);
  }

  get ledgerAccounts(): resources.LedgerAccounts {
    return new resources.LedgerAccounts(this);
  }

  get linkedIntegrations(): resources.LinkedIntegrations {
    return new resources.LinkedIntegrations(this);
  }

  get notes(): resources.Notes {
    return new resources.Notes(this);
  }

  get outreachGroups(): resources.OutreachGroups {
    return new resources.OutreachGroups(this);
  }

  get outreachWorkflows(): resources.OutreachWorkflows {
    return new resources.OutreachWorkflows(this);
  }

  get organizations(): resources.Organizations {
    return new resources.Organizations(this);
  }

  get paymentSettings(): resources.PaymentSettingsResource {
    return new resources.PaymentSettingsResource(this);
  }

  get partners(): resources.Partners {
    return new resources.Partners(this);
  }

  get dunningStates(): resources.DunningStates {
    return new resources.DunningStates(this);
  }

  get payments(): resources.Payments {
    return new resources.Payments(this);
  }

  get phoneNumbers(): resources.PhoneNumbers {
    return new resources.PhoneNumbers(this);
  }

  get profile(): resources.Profiles {
    return new resources.Profiles(this);
  }

  get remittances(): resources.Remittances {
    return new resources.Remittances(this);
  }

  get tasks(): resources.Tasks {
    return new resources.Tasks(this);
  }

  get taskCollections(): resources.TaskCollections {
    return new resources.TaskCollections(this);
  }

  get taskRequirements(): resources.TaskRequirements {
    return new resources.TaskRequirements(this);
  }

  get userInvites(): resources.UserInvites {
    return new resources.UserInvites(this);
  }

  get users(): resources.Users {
    return new resources.Users(this);
  }

  get stripeConnectAccounts(): resources.StripeConnectAccounts {
    return new resources.StripeConnectAccounts(this);
  }

  get knowledgeEntries(): resources.KnowledgeEntries {
    return new resources.KnowledgeEntries(this);
  }

  get tags(): resources.Tags {
    return new resources.Tags(this);
  }

  get vendors(): resources.Vendors {
    return new resources.Vendors(this);
  }

  async getFeatureFlags(): Promise<Record<string, any>> {
    try {
      const response = await this.client.get('v1/feature-flags');
      return response.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async toggleFeatureFlag(flag: string, value: any) {
    try {
      await this.client.post(`v1/feature-flags/${flag}`, {
        enabled: value,
      });
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async uploadAttachment(
    attachment: Attachment,
    presignedUrl: PresignedUrl,
    file?: File | null,
    onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void,
  ) {
    if (!attachment._file) {
      if (!file) {
        throw Error('File is required');
      }

      attachment._file = file;
    }

    attachment.is_uploading = true;

    const payload = new FormData();
    const headers: Record<string, string> = {
      'Access-Control-Allow-Origin': '*',
    };
    Object.entries(presignedUrl.fields).forEach(([key, val]) => {
      payload.append(key, val);
    });

    payload.append('file', attachment._file);

    try {
      return axios.post(presignedUrl.url, payload, {
        headers,
        onUploadProgress: (progressEvent) => {
          if (onUploadProgress) {
            onUploadProgress(progressEvent);
            return;
          }
        },
      });
    } catch (e) {
      attachment.failed_to_upload = true;
      console.error(e);
      throw e;
    } finally {
      attachment.is_uploading = false;
    }
  }
}
